import { ui } from '@procuros/datachecks';

import { generateSheetNameHash } from 'services/excel/utils/sheetNameUtils';
import { logError } from 'services/logging/logging';
import { getModificationFieldKey, isRelativeModificationForExcel } from 'services/webedi/modificationGroupHelpers';
import { fromStringDateToProcurosTimestamp, parseFlexibleDate } from 'support/helpers/dateTime/dateTime';
import { getValidNumberValue } from 'support/helpers/numbers/numbers';

export const isEmpty = (val: unknown): boolean => val === null || val === undefined || val === '';

export const resolveModificationFieldKey = (
  columnKey: string,
  embeddedTableKey: string,
  row: Record<string, unknown>,
  sheets: Record<string, Array<Record<string, unknown>>>,
): string => {
  if (!columnKey.includes('amount') || !columnKey.includes('modification_groups')) {
    return embeddedTableKey;
  }

  const typeKey = columnKey.replace(/\.\*\..*$/, `.*.type`);
  const typeValue = row[typeKey];
  const transformedType = transformValue(typeValue, ui.FieldTypes.select, sheets, typeKey);

  const rowWithTransformedType = { ...row, [typeKey]: transformedType };
  const isRelativeModification = isRelativeModificationForExcel(columnKey, rowWithTransformedType);
  return getModificationFieldKey(embeddedTableKey, isRelativeModification);
};

const transformNumericValue = (value: unknown, key?: string): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const validNumber = getValidNumberValue(value);
    if (validNumber === undefined || isNaN(validNumber)) {
      logError(
        `Failed to convert value "${value}" to number for ${key ? `column "${key}"` : 'unknown column'}. Expected numeric value.`,
      );
      return undefined;
    }
    return Number(validNumber);
  }

  logError(
    `Invalid type for numeric field ${key ? `"${key}"` : ''}. Received ${typeof value}, expected number or string.`,
  );
  return undefined;
};

const transformDateValue = (value: unknown, key?: string): number | null => {
  try {
    const date = value instanceof Date ? value : parseFlexibleDate(value as string);
    return fromStringDateToProcurosTimestamp(date);
  } catch (error) {
    logError(
      `Failed to parse date value "${value}" for ${key ? `column "${key}"` : 'unknown column'}. Error: ${error instanceof Error ? error.message : String(error)}`,
    );
    return null;
  }
};

const transformSelectValue = (
  value: unknown,
  sheets?: Record<string, Array<Record<string, unknown>>>,
  key?: string,
): unknown => {
  if (sheets && key) {
    const sheetHash = generateSheetNameHash(key);
    if (sheets[sheetHash]) {
      return sheets[sheetHash].find((option) => option['label'] === value)?.value;
    }
  }
  return value;
};

export const transformValue = (
  value: unknown,
  format: ui.FieldTypes,
  sheets?: Record<string, Array<Record<string, unknown>>>,
  key?: string,
): unknown => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  switch (format) {
    case ui.FieldTypes.boolean:
      return value === 'true' || value === true;

    case ui.FieldTypes.number:
    case ui.FieldTypes.monetary:
      return transformNumericValue(value, key);

    case ui.FieldTypes.date:
    case ui.FieldTypes.date_time:
      return transformDateValue(value, key);

    case ui.FieldTypes.text:
      return String(value);

    case ui.FieldTypes.select:
    case ui.FieldTypes.multi_select:
      return transformSelectValue(value, sheets, key);

    default:
      return value;
  }
};

export const areEmbeddedTableRowsEqual = (item1: Record<string, unknown>, item2: Record<string, unknown>): boolean => {
  return JSON.stringify(item1) === JSON.stringify(item2);
};
